// ============================================================================
// SPECIALIST ADMIN WORKPLACE VIEW
//
// Role: specialist_admin only (RouteGuard enforces before this renders).
//
// THREE TABS — default landing = Today:
//
//   Today            — SEEN referrals where appointmentDate = today
//                      Sort: priority desc → referralId asc
//
//   Needs Close Loop — SEEN referrals where appointmentDate < today
//                      (appointment in the past, loop not yet closed)
//                      Sort: oldest appointmentDate first → priority → referralId
//
//   Closed Loop      — CLOSED_LOOP referrals, lazy-loaded on first tab open
//                      Sort: closedAt desc
//
// specialist_admin sees all consultants in their specialties (no consultantId filter).
//
// Primary action: "Close Referral Loop" (SEEN → CLOSED_LOOP via closeLoop()).
// No "Mark as Seen" — that is specialist-only.
//
// Zero-PHI invariant: no patient name/DOB/MRN in workflow docs or list items.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Stethoscope, Loader2, AlertCircle, RefreshCw,
  Calendar, User, CheckCircle2, X, ClipboardList,
  ChevronRight, FileText, Activity, Eye,
  Hash, Shield, AlertTriangle, RotateCcw,
  GitBranch, Upload, Paperclip, Bell,
} from 'lucide-react';
import {
  WORKFLOW_STATUS,
  WORKFLOW_PRIORITY,
  WORKFLOW_EVENT_TYPES,
  ROLE_DISPLAY,
  listWorkqueueItems,
  listWorkflowEvents,
  REWORK_ITEM_TYPE_LABELS,
  REWORK_REQUEST_STATUS,
  listReworkRequests,
  getUser,
  createAttachmentUploadUrl,
  finalizeAttachmentUpload,
  COMMUNICATION_PURPOSE,
} from '../core/index.js';
import { getReferral } from '../core/store.referrals.js';
import { findReferralInBuckets, classifyDeepLinkFailure } from '../core/deepLinkReferral.js';
import { canCreateReferralPing } from '../core/rbac.js';
import { closeLoop } from '../core/store.scheduling.js';
import { useAuth } from '../auth/AuthContext.jsx';
import PingComposer from './PingComposer.jsx';

// ── Constants ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

// ── Display config ─────────────────────────────────────────────────────────────

const PRIORITY_CONFIG = {
  [WORKFLOW_PRIORITY.NORMAL]: { label: 'Normal', tw: 'bg-slate-700/60 text-slate-400 border-slate-600/30' },
  [WORKFLOW_PRIORITY.HIGH]:   { label: 'High',   tw: 'bg-orange-500/15 text-orange-300 border-orange-500/20' },
  [WORKFLOW_PRIORITY.URGENT]: { label: 'Urgent', tw: 'bg-red-500/15 text-red-300 border-red-500/20' },
};

const PRIORITY_RANK = {
  [WORKFLOW_PRIORITY.URGENT]: 0,
  [WORKFLOW_PRIORITY.HIGH]:   1,
  [WORKFLOW_PRIORITY.NORMAL]: 2,
};

const OUTCOME_CONFIG = {
  URGENT:  { label: 'URGENT',  tw: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ROUTINE: { label: 'ROUTINE', tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  NOT_YET: { label: 'NOT YET', tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
  urgent:  { label: 'URGENT',  tw: 'bg-red-500/20 text-red-300 border-red-500/30' },
  routine: { label: 'ROUTINE', tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  not_yet: { label: 'NOT YET', tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
};

const STATUS_CONFIG = {
  NEW:               { label: 'New',               tw: 'bg-blue-500/15 text-blue-300 border-blue-500/20' },
  NEEDS_INFO:        { label: 'Needs Info',         tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
  READY_TO_SCHEDULE: { label: 'Ready to Schedule',  tw: 'bg-teal-500/15 text-teal-300 border-teal-500/20' },
  SCHEDULED:         { label: 'Scheduled',          tw: 'bg-blue-500/15 text-blue-300 border-blue-500/20' },
  SEEN:              { label: 'Seen',               tw: 'bg-purple-500/15 text-purple-300 border-purple-500/20' },
  CLOSED_LOOP:       { label: 'Closed Loop',        tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
};

const REWORK_STATUS_CONFIG = {
  [REWORK_REQUEST_STATUS.OPEN]:      { label: 'Open',      tw: 'bg-amber-500/15 text-amber-300 border-amber-500/20' },
  [REWORK_REQUEST_STATUS.RECEIVED]:  { label: 'Received',  tw: 'bg-teal-500/15 text-teal-300 border-teal-500/20' },
  [REWORK_REQUEST_STATUS.RESOLVED]:  { label: 'Resolved',  tw: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20' },
  [REWORK_REQUEST_STATUS.CANCELLED]: { label: 'Cancelled', tw: 'bg-slate-700/60 text-slate-500 border-slate-600/30' },
};

const EVENT_CONFIG = {
  [WORKFLOW_EVENT_TYPES.WORKFLOW_CREATED]: { label: 'Referral Created',       icon: FileText,     color: 'text-slate-400'  },
  [WORKFLOW_EVENT_TYPES.OWNER_ASSIGNED]:   { label: 'Claimed by Coordinator', icon: User,         color: 'text-blue-400'   },
  [WORKFLOW_EVENT_TYPES.OWNER_UNASSIGNED]: { label: 'Owner Released',         icon: User,         color: 'text-slate-400'  },
  [WORKFLOW_EVENT_TYPES.STATUS_CHANGED]:   { label: 'Status Changed',         icon: Activity,     color: 'text-teal-400'   },
  [WORKFLOW_EVENT_TYPES.TRIAGE_UPDATED]:   { label: 'Triage Updated',         icon: Stethoscope,  color: 'text-purple-400' },
  [WORKFLOW_EVENT_TYPES.SCHEDULED_SET]:    { label: 'Appointment Scheduled',  icon: Calendar,     color: 'text-blue-400'   },
  [WORKFLOW_EVENT_TYPES.SEEN_SET]:         { label: 'Marked Seen',            icon: Eye,          color: 'text-teal-400'   },
  [WORKFLOW_EVENT_TYPES.CLOSED_LOOP_SET]:  { label: 'Consult Completed',      icon: CheckCircle2, color: 'text-emerald-400'},
  PRIORITY_CHANGED:                        { label: 'Priority Changed',        icon: AlertTriangle,color: 'text-orange-400' },
};

const VISIT_TYPE_LABELS = {
  initial_consultation: 'Initial Consultation',
  follow_up:            'Follow Up',
  second_opinion:       'Second Opinion',
  urgent_review:        'Urgent Review',
};

const VISIT_PURPOSE_LABELS = {
  new_complaint:      'New Complaint',
  ongoing_management: 'Ongoing Management',
  post_procedure:     'Post Procedure',
  pre_procedure:      'Pre Procedure',
  other:              'Other',
};

const TRIAGE_DECISION_LABELS = {
  none:          'None',
  ok:            'OK',
  urgent_review: 'Urgent Review',
  redirect:      'Redirect',
};

// ── Tab definitions ────────────────────────────────────────────────────────────

const TABS = [
  {
    id:          'today',
    label:       'Today',
    activeColor: 'text-teal-400 border-teal-500',
    emptyIcon:   Eye,
    emptyTitle:  'No patients seen today',
    emptyBody:   'Referrals seen today will appear here, ready to close the loop.',
  },
  {
    id:          'needs_close_loop',
    label:       'Needs Close Loop',
    activeColor: 'text-amber-400 border-amber-500',
    emptyIcon:   CheckCircle2,
    emptyTitle:  'All loops closed',
    emptyBody:   'Referrals from past appointments that still need loop closure will appear here.',
  },
  {
    id:          'closed_loop',
    label:       'Closed Loop',
    activeColor: 'text-emerald-400 border-emerald-500',
    emptyIcon:   CheckCircle2,
    emptyTitle:  'No closed referrals',
    emptyBody:   'Completed referrals with closed loops will appear here.',
  },
];

// ── Utilities ──────────────────────────────────────────────────────────────────

function tsSec(ts) {
  if (!ts) return null;
  if (typeof ts.seconds === 'number') return ts.seconds;
  if (typeof ts.toMillis === 'function') return ts.toMillis() / 1000;
  return null;
}

function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function toLocalDateStr(ts) {
  const d = tsToDate(ts);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Classify a SEEN item into today / needs_close_loop / future based on appointmentDate. */
function classifySeenItem(item) {
  const apptDate = item.workflow?.scheduling?.appointmentDate ?? null;
  if (!apptDate) return 'needs_close_loop';
  const today = todayLocal();
  if (apptDate === today) return 'today';
  if (apptDate < today)  return 'needs_close_loop';
  return 'future';
}

function sortNeedsCloseLoop(a, b) {
  const dateA = a.workflow?.scheduling?.appointmentDate ?? '';
  const dateB = b.workflow?.scheduling?.appointmentDate ?? '';
  if (dateA !== dateB) return dateA.localeCompare(dateB); // oldest appointment first
  const pDiff = (PRIORITY_RANK[a.workflow?.priority] ?? 2) - (PRIORITY_RANK[b.workflow?.priority] ?? 2);
  if (pDiff !== 0) return pDiff;
  return (a.referral?.id ?? '').localeCompare(b.referral?.id ?? '');
}

function sortToday(a, b) {
  const pDiff = (PRIORITY_RANK[a.workflow?.priority] ?? 2) - (PRIORITY_RANK[b.workflow?.priority] ?? 2);
  if (pDiff !== 0) return pDiff;
  return (a.referral?.id ?? '').localeCompare(b.referral?.id ?? '');
}

function sortClosed(arr) {
  return [...arr].sort((a, b) => {
    const ca = tsSec(a.workflow?.closedLoop?.closedAt);
    const cb = tsSec(b.workflow?.closedLoop?.closedAt);
    if (ca !== cb) { if (ca === null) return 1; if (cb === null) return -1; return cb - ca; }
    return (tsSec(b.workflow?.updatedAt) ?? 0) - (tsSec(a.workflow?.updatedAt) ?? 0);
  });
}

function fmtLabel(str) {
  if (!str) return '—';
  return str.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtTs(ts) {
  if (!ts) return '—';
  const d = tsToDate(ts);
  if (!d) return '—';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = tsToDate(ts);
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function from24h(str) {
  if (!str) return null;
  const [h, m] = str.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return str;
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour   = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

function fmtAnswerValue(val) {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) {
    if (val.length === 0) return '—';
    return val.map((v) => fmtLabel(String(v))).join(', ');
  }
  if (typeof val === 'object' && val.seconds !== undefined) return fmtTs(val);
  return fmtLabel(String(val));
}

// ── Shared sub-components ──────────────────────────────────────────────────────

function Badge({ tw, label }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${tw}`}>
      {label}
    </span>
  );
}

function DetailRow({ label, value, mono = false, dim = false }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      <span className="text-xs text-slate-500 w-40 flex-shrink-0 pt-0.5 leading-relaxed">{label}</span>
      <span className={`text-xs flex-1 leading-relaxed break-words
        ${mono ? 'font-mono' : ''}
        ${dim ? 'text-slate-500 italic' : 'text-slate-200'}`}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function SectionHeading({ icon: Icon, label, count }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-3.5 h-3.5 flex-shrink-0 text-slate-500" />
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </span>
      {count != null && (
        <span className="ml-auto text-[11px] text-slate-600 font-medium">{count}</span>
      )}
    </div>
  );
}

function SectionCard({ children }) {
  return (
    <div className="rounded-xl border px-4 py-3 divide-y bg-slate-800/40 border-slate-700/30 divide-slate-700/20">
      {children}
    </div>
  );
}

function EmptyState({ message }) {
  return <p className="text-xs text-slate-600 italic py-2 px-1">{message}</p>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtBytes(bytes) {
  if (!bytes || bytes < 1) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Consult Document Upload ───────────────────────────────────────────────────

const CONSULT_DOC_ACCEPT = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
].join(',');

const CONSULT_DOC_MAX_SIZE = 25 * 1024 * 1024;

function ConsultDocumentUpload({ referralId, onUploaded }) {
  const [file,     setFile]     = useState(null);
  const [phase,    setPhase]    = useState('idle');
  const [progress, setProgress] = useState(0);
  const [errMsg,   setErrMsg]   = useState(null);

  const reset = () => {
    setFile(null);
    setPhase('idle');
    setProgress(0);
    setErrMsg(null);
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > CONSULT_DOC_MAX_SIZE) {
      setErrMsg(`File exceeds 25 MB limit (${fmtBytes(selected.size)}).`);
      return;
    }
    setFile(selected);
    setErrMsg(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setPhase('uploading');
    setProgress(0);
    setErrMsg(null);

    try {
      const { attachmentId, uploadUrl } = await createAttachmentUploadUrl(referralId, {
        mimeType:     file.type,
        originalName: file.name,
        type:         'CONSULT_DOCUMENT',
        category:     'CONSULT_DOCUMENT',
        purpose:      'CLOSED_LOOP_EVIDENCE',
      });

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload  = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (HTTP ${xhr.status})`));
        xhr.onerror = () => reject(new Error('Network error during upload.'));
        xhr.send(file);
      });

      setPhase('finalizing');
      await finalizeAttachmentUpload(referralId, attachmentId);

      setPhase('done');
      if (onUploaded) onUploaded();
    } catch (err) {
      setPhase('error');
      setErrMsg(err.message ?? 'Upload failed. Please retry.');
    }
  };

  const isDone  = phase === 'done';
  const isBusy  = phase === 'uploading' || phase === 'finalizing';
  const isError = phase === 'error';

  return (
    <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Upload className="w-4 h-4 text-emerald-400" />
        <span className="text-xs font-semibold text-emerald-300">Upload Consult Document</span>
      </div>

      {isDone ? (
        <div className="flex items-center gap-2 py-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span className="text-xs text-emerald-300">Consult document uploaded and verified.</span>
        </div>
      ) : (
        <>
          {!file ? (
            <label className="flex flex-col items-center justify-center gap-2 py-5 rounded-lg border-2 border-dashed border-slate-600/50 hover:border-emerald-500/40 cursor-pointer transition-colors">
              <Paperclip className="w-5 h-5 text-slate-500" />
              <span className="text-xs text-slate-400">Click to select a file</span>
              <span className="text-[11px] text-slate-600">PDF, Word, or image · 25 MB max</span>
              <input type="file" accept={CONSULT_DOC_ACCEPT} onChange={handleFileChange} className="hidden" />
            </label>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2.5">
                <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-slate-200 truncate">{file.name}</p>
                  <p className="text-[11px] text-slate-600">{fmtBytes(file.size)}</p>
                </div>
                {!isBusy && (
                  <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {isBusy && (
                <div className="space-y-1.5">
                  <div className="w-full h-1.5 rounded-full bg-slate-700 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all duration-300"
                      style={{ width: `${phase === 'finalizing' ? 100 : progress}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {phase === 'finalizing' ? 'Verifying upload…' : `Uploading… ${progress}%`}
                  </p>
                </div>
              )}

              {!isBusy && (
                <button
                  onClick={handleUpload}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold transition-colors"
                >
                  <Upload className="w-3.5 h-3.5" /> Upload Document
                </button>
              )}
            </div>
          )}

          {isError && errMsg && (
            <div className="flex items-start gap-2 mt-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/20">
              <AlertCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-300">{errMsg}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Admin Close Loop Drawer ────────────────────────────────────────────────────
// specialist_admin variant: SEEN → CLOSED_LOOP only. No "Mark as Seen".

function AdminCloseLoopDrawer({ item, uid, role, onClose, onCompleted, readOnly, activeFocusTarget }) {
  const { referral, workflow } = item;
  const tenantId   = referral?.tenantId;
  const referralId = referral?.id;

  // ── Sub-data ────────────────────────────────────────────────────────────────
  const [events,     setEvents]     = useState([]);
  const [rework,     setRework]     = useState([]);
  const [userNames,  setUserNames]  = useState({});
  const [subLoading, setSubLoading] = useState(true);

  const loadSubData = useCallback(() => {
    if (!tenantId || !referralId) { setSubLoading(false); return; }
    setSubLoading(true);

    Promise.all([
      listWorkflowEvents(tenantId, referralId).catch((e) => { console.warn('[AdminDrawer] events:', e?.message); return []; }),
      listReworkRequests(tenantId, referralId).catch((e) => { console.warn('[AdminDrawer] rework:', e?.message); return []; }),
    ]).then(([evts, rwk]) => {
      setEvents(evts ?? []);
      setRework(rwk ?? []);

      const uids = new Set();
      const addUid = (u) => { if (u && typeof u === 'string') uids.add(u); };
      addUid(referral?.createdByUid);
      addUid(workflow?.ownerUid);
      addUid(workflow?.scheduling?.scheduledByUid);
      addUid(workflow?.scheduling?.scheduledBy);
      addUid(workflow?.closedLoop?.closedByUid);
      (evts ?? []).forEach((e) => addUid(e.actorUid));

      if (uids.size === 0) { setSubLoading(false); return; }

      Promise.all(
        [...uids].map((u) =>
          getUser(u).then((doc) => (doc?.name ? [u, doc.name] : null)).catch(() => null)
        )
      ).then((results) => {
        const map = {};
        results.forEach((entry) => { if (entry) map[entry[0]] = entry[1]; });
        setUserNames(map);
        setSubLoading(false);
      });
    });
  }, [tenantId, referralId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadSubData(); }, [loadSubData]);

  const displayName = useCallback(
    (u) => (u ? (userNames[u] ?? u) : '—'),
    [userNames],
  );

  // ── Derived values ──────────────────────────────────────────────────────────
  const finalOutcome        = referral?.finalOutcome ?? referral?.outcome?.status ?? null;
  const outcomeReason       = referral?.outcome?.reason ?? null;
  const outcomeCfg          = finalOutcome ? (OUTCOME_CONFIG[finalOutcome] ?? null) : null;
  const triggeredRedFlagIds = Array.isArray(referral?.triggeredRedFlagIds) ? referral.triggeredRedFlagIds : [];
  const missingFieldIds     = Array.isArray(referral?.missingFieldIds)     ? referral.missingFieldIds     : [];
  const receiptToken        = referral?.receiptToken ?? null;

  const workflowStatus    = workflow?.status ?? null;
  const statusCfg         = workflowStatus ? (STATUS_CONFIG[workflowStatus] ?? null) : null;
  const priority          = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const priorityCfg       = PRIORITY_CONFIG[priority];
  const ownerUid          = workflow?.ownerUid ?? null;
  const missingItemsCount = workflow?.rework?.missingItemsCount ?? 0;

  const apptDate       = workflow?.scheduling?.appointmentDate ?? null;
  const apptTime       = workflow?.scheduling?.appointmentTime ?? null;
  const consultantName = workflow?.scheduling?.consultantName ?? workflow?.scheduling?.consultantId ?? null;
  const locationId     = workflow?.scheduling?.locationId ?? null;
  const schedulingNote = workflow?.scheduling?.schedulingNote ?? null;
  const scheduledAt    = workflow?.scheduling?.scheduledAt ?? null;
  const scheduledByUid = workflow?.scheduling?.scheduledByUid ?? workflow?.scheduling?.scheduledBy ?? null;
  const seenAt         = workflow?.scheduling?.seenAt ?? null;
  const hasAppt        = apptDate || scheduledAt;

  const visitType           = referral?.visitType ?? referral?.patient?.visitType ?? null;
  const reasonForEscalation = referral?.reasonForEscalation ?? referral?.patient?.reasonForEscalation ?? null;
  const visitPurpose        = referral?.visitPurpose ?? referral?.patient?.visitPurpose ?? null;
  const localId             = referral?.localId ?? null;
  const hasVisitInfo        = visitType || reasonForEscalation || visitPurpose;

  const rawAnswers    = referral?.clinicalAnswers ?? referral?.answers ?? null;
  const answerEntries = rawAnswers && typeof rawAnswers === 'object'
    ? Object.entries(rawAnswers).filter(([, v]) => v !== null && v !== undefined && v !== '')
    : [];

  const triageDecision   = workflow?.triage?.triageDecision ?? null;
  const triageNote       = workflow?.triageNote ?? null;
  const overrideDecision = workflow?.overrideDecision ?? null;
  const overrideReason   = workflow?.overrideReason ?? null;
  const overrideNote     = workflow?.overrideNote ?? null;
  const hasTriageData    = (triageDecision && triageDecision !== 'none')
    || triageNote || overrideDecision || overrideReason || overrideNote;

  const closedAt       = workflow?.closedLoop?.closedAt ?? null;
  const closedByUid    = workflow?.closedLoop?.closedByUid ?? null;
  const closedLoopNote = workflow?.closedLoop?.closedLoopNote ?? null;

  // ── Consult document state ───────────────────────────────────────────────────
  const [hasConsultDoc, setHasConsultDoc] = useState(workflow?.closedLoop?.hasConsultDocument === true);

  const handleConsultDocUploaded = useCallback(() => {
    setHasConsultDoc(true);
    loadSubData();
  }, [loadSubData]);

  // ── Close Referral Loop ─────────────────────────────────────────────────────
  const [note,      setNote]      = useState('');
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);

  const isSeen = workflowStatus === WORKFLOW_STATUS.SEEN;

  const handleCloseLoop = async () => {
    setSaveError(null);
    setSaving(true);
    try {
      await closeLoop(
        tenantId, referralId,
        { closedLoopNote: note.trim() || '' },
        uid, role,
      );
      onCompleted(referralId);
      onClose();
    } catch (err) {
      console.error('[AdminCloseLoopDrawer] closeLoop failed:', err);
      setSaveError(safeUserError(err) ?? 'Failed to close loop. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const scrollBodyRef = useRef(null);

  useEffect(() => {
    if (!activeFocusTarget || subLoading) return;
    const timer = setTimeout(() => {
      const container = scrollBodyRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
      if (!el) return;
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const offsetTop = elRect.top - containerRect.top + container.scrollTop;
      container.scrollTo({ top: offsetTop - containerRect.height / 2 + elRect.height / 2, behavior: 'smooth' });
    }, 150);
    return () => clearTimeout(timer);
  }, [activeFocusTarget, subLoading]);

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={() => !saving && onClose()} aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl flex flex-col bg-slate-900 border-l border-slate-700/60 shadow-2xl">

        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-slate-800/60 flex-shrink-0 bg-slate-900/95">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[11px] text-slate-500 font-mono">{referralId ?? '—'}</span>
              {receiptToken && <><span className="text-slate-700">·</span><span className="text-[11px] text-slate-500 font-mono">{receiptToken}</span></>}
            </div>
            <h2 className="text-base font-semibold text-white leading-tight">
              {fmtLabel(referral?.specialtyId)}
              {referral?.packId && <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>}
            </h2>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {outcomeCfg  && <Badge tw={outcomeCfg.tw}  label={outcomeCfg.label} />}
              {priorityCfg && <Badge tw={priorityCfg.tw} label={priorityCfg.label} />}
              {statusCfg   && <Badge tw={statusCfg.tw}   label={statusCfg.label} />}
              {readOnly    && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400 font-medium"><CheckCircle2 className="w-3 h-3" /> Closed</span>}
            </div>
          </div>
          <button onClick={() => !saving && onClose()} disabled={saving}
            className="p-2 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-colors flex-shrink-0 disabled:opacity-50" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div ref={scrollBodyRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* Gate Decision */}
          <div>
            <SectionHeading icon={Shield} label="Gate Decision" />
            <SectionCard>
              <DetailRow label="Gate Outcome"  value={outcomeCfg?.label ?? fmtLabel(finalOutcome)} />
              {outcomeReason && <DetailRow label="Outcome Reason" value={outcomeReason} />}
              {triggeredRedFlagIds.length > 0 && <DetailRow label={`Red Flags (${triggeredRedFlagIds.length})`} value={triggeredRedFlagIds.map(fmtLabel).join(', ')} />}
              {missingFieldIds.length > 0     && <DetailRow label={`Missing at Submit (${missingFieldIds.length})`} value={missingFieldIds.map(fmtLabel).join(', ')} dim />}
              {receiptToken && <DetailRow label="EHR Token" value={receiptToken} mono />}
            </SectionCard>
          </div>

          {/* Referral Details */}
          <div>
            <SectionHeading icon={Hash} label="Referral Details" />
            <SectionCard>
              <DetailRow label="Specialty"       value={fmtLabel(referral?.specialtyId)} />
              <DetailRow label="Pack"            value={fmtLabel(referral?.packId)} />
              {referral?.versionId && <DetailRow label="Pack Version"  value={referral.versionId} mono />}
              <DetailRow label="Referral ID"     value={referralId} mono />
              <DetailRow label="Submitted"       value={fmtTs(referral?.createdAt)} />
              {referral?.createdByUid && <DetailRow label="Submitted By"  value={displayName(referral.createdByUid)} />}
              <DetailRow label="Workflow Status" value={statusCfg?.label ?? fmtLabel(workflowStatus)} />
              {workflow?.statusUpdatedAt && <DetailRow label="Status Since" value={fmtTs(workflow.statusUpdatedAt)} />}
              {ownerUid          && <DetailRow label="Assigned To"   value={displayName(ownerUid)} />}
              {missingItemsCount > 0 && <DetailRow label="Missing Items" value={String(missingItemsCount)} />}
            </SectionCard>
          </div>

          {/* Visit Information */}
          {(hasVisitInfo || localId) && (
            <div>
              <SectionHeading icon={FileText} label="Visit Information" />
              <SectionCard>
                {visitType && <DetailRow label="Visit Type" value={VISIT_TYPE_LABELS[visitType] ?? fmtLabel(visitType)} />}
                {reasonForEscalation && (
                  <DetailRow label="Reason for Escalation"
                    value={Array.isArray(reasonForEscalation) ? reasonForEscalation.map(fmtLabel).join(', ') : fmtLabel(reasonForEscalation)} />
                )}
                {visitPurpose && <DetailRow label="Visit Purpose" value={VISIT_PURPOSE_LABELS[visitPurpose] ?? fmtLabel(visitPurpose)} />}
                {localId      && <DetailRow label="Local Reference" value={localId} mono />}
              </SectionCard>
            </div>
          )}

          {/* Clinical Answers */}
          <div>
            <SectionHeading icon={ClipboardList} label="Clinical Answers"
              count={answerEntries.length > 0 ? `${answerEntries.length} fields` : null} />
            {answerEntries.length > 0 ? (
              <SectionCard>
                {answerEntries.map(([key, val]) => (
                  <DetailRow key={key} label={fmtLabel(key)} value={fmtAnswerValue(val)} />
                ))}
              </SectionCard>
            ) : (
              <EmptyState message="No clinical answers recorded for this referral." />
            )}
          </div>

          {/* Appointment */}
          {hasAppt && (
            <div>
              <SectionHeading icon={Calendar} label="Appointment" />
              <SectionCard>
                {apptDate ? (
                  <DetailRow label="Date & Time"
                    value={`${new Date(apptDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${apptTime ? ' · ' + (from24h(apptTime) ?? apptTime) : ''}`} />
                ) : (
                  scheduledAt && <DetailRow label="Booked At" value={fmtTs(scheduledAt)} />
                )}
                {consultantName && <DetailRow label="Specialist"      value={consultantName} />}
                {locationId     && <DetailRow label="Location"        value={locationId} />}
                {schedulingNote && <DetailRow label="Scheduling Note" value={schedulingNote} />}
                {scheduledAt && apptDate && <DetailRow label="Booked At"  value={fmtTs(scheduledAt)} />}
                {scheduledByUid && <DetailRow label="Booked By"       value={displayName(scheduledByUid)} />}
                {seenAt && <DetailRow label="Seen At" value={fmtTs(seenAt)} />}
              </SectionCard>
            </div>
          )}

          {/* Triage Annotations */}
          {hasTriageData && (
            <div>
              <SectionHeading icon={Stethoscope} label="Triage Annotations" />
              <SectionCard>
                {triageDecision && triageDecision !== 'none' && <DetailRow label="Triage Decision" value={TRIAGE_DECISION_LABELS[triageDecision] ?? fmtLabel(triageDecision)} />}
                {triageNote      && <DetailRow label="Triage Note"       value={triageNote} />}
                {overrideDecision && <DetailRow label="Override Decision" value={fmtLabel(overrideDecision)} />}
                {overrideReason  && <DetailRow label="Override Reason"   value={overrideReason} />}
                {overrideNote    && <DetailRow label="Override Note"     value={overrideNote} />}
              </SectionCard>
            </div>
          )}

          {/* Rework Items */}
          <div>
            <SectionHeading icon={RotateCcw} label="Rework Items"
              count={!subLoading && rework.length > 0 ? `${rework.length}` : null} />
            {subLoading ? (
              <div className="flex items-center gap-2 py-3"><Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" /><span className="text-xs text-slate-600">Loading…</span></div>
            ) : rework.length === 0 ? <EmptyState message="No rework requests for this referral." />
            : (
              <div className="space-y-1.5">
                {rework.map((req) => {
                  const sCfg      = REWORK_STATUS_CONFIG[req.status] ?? REWORK_STATUS_CONFIG.open;
                  const typeLabel = REWORK_ITEM_TYPE_LABELS[req.itemType] ?? fmtLabel(req.itemType ?? 'Unknown');
                  return (
                    <div key={req.id ?? req.requestId} className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-800/40 border border-slate-700/30">
                      <span className="text-xs text-slate-200 font-medium">{typeLabel}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] text-slate-600">{fmtDate(req.createdAt)}</span>
                        <Badge tw={sCfg.tw} label={sCfg.label} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Workflow History */}
          <div>
            <SectionHeading icon={GitBranch} label="Workflow History"
              count={!subLoading && events.length > 0 ? `${events.length} events` : null} />
            {subLoading ? (
              <div className="flex items-center gap-2 py-3"><Loader2 className="w-3.5 h-3.5 text-slate-500 animate-spin" /><span className="text-xs text-slate-600">Loading…</span></div>
            ) : events.length === 0 ? <EmptyState message="No workflow events recorded." />
            : (
              <div className="relative pl-4">
                <div className="absolute left-4 top-2 bottom-2 w-px bg-slate-800" />
                {events.map((evt, idx) => {
                  const cfg  = EVENT_CONFIG[evt.eventType] ?? { label: fmtLabel(evt.eventType ?? 'Event'), icon: Activity, color: 'text-slate-400' };
                  const Icon = cfg.icon;
                  return (
                    <div key={evt.id ?? idx} className="relative flex items-start gap-3 py-2.5">
                      <div className="relative z-10 w-5 h-5 rounded-full bg-slate-800 border border-slate-700/60 flex items-center justify-center flex-shrink-0 -ml-2.5">
                        <Icon className={`w-2.5 h-2.5 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0 pt-0.5">
                        <p className="text-xs font-medium text-slate-200">
                          {cfg.label}
                          {evt.fromStatus && evt.toStatus && (
                            <span className="text-slate-500 font-normal"> · {STATUS_CONFIG[evt.fromStatus]?.label ?? evt.fromStatus} → {STATUS_CONFIG[evt.toStatus]?.label ?? evt.toStatus}</span>
                          )}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                          <span className="text-[11px] text-slate-600">{fmtTs(evt.createdAt)}</span>
                          {evt.actorUid && (
                            <span className="text-[11px] text-slate-500">
                              · {displayName(evt.actorUid)}
                              {evt.actorRole && <span className="text-slate-600"> ({ROLE_DISPLAY[evt.actorRole]?.name ?? fmtLabel(evt.actorRole)})</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Consult Document Upload — SEEN referrals only */}
          {isSeen && !readOnly && (
            <div data-learning-target="specialist-admin-consult-upload" className={activeFocusTarget === 'specialist-admin-consult-upload' ? 'rounded-xl ring-2 ring-emerald-400/20' : ''}>
              <ConsultDocumentUpload referralId={referralId} onUploaded={handleConsultDocUploaded} />
            </div>
          )}

          {/* Consult Completed (readOnly) */}
          {readOnly && closedAt && (
            <div>
              <SectionHeading icon={CheckCircle2} label="Consult Completed" />
              <div className="bg-emerald-500/5 rounded-xl border border-emerald-500/20 px-4 py-3 divide-y divide-emerald-500/10">
                <DetailRow label="Closed At"    value={fmtTs(closedAt)} />
                {closedByUid  && <DetailRow label="Closed By"    value={displayName(closedByUid)} />}
                {closedLoopNote
                  ? <DetailRow label="Consult Note" value={closedLoopNote} />
                  : <DetailRow label="Consult Note" value="No note recorded." dim />}
              </div>
            </div>
          )}

          {/* Referral Ping — notify another user (e.g. GP) the closed-loop summary is ready */}
          {workflow && canCreateReferralPing(role) && (
            <div
              data-learning-target="specialist-admin-referral-ping"
              className={activeFocusTarget === 'specialist-admin-referral-ping' ? 'rounded-xl ring-2 ring-emerald-400/20' : ''}
            >
              <SectionHeading icon={Bell} label="Referral Ping" />
              <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-700/30">
                <PingComposer
                  tenantId={tenantId}
                  referralId={referralId}
                  workflow={workflow}
                  uid={uid}
                  role={role}
                  defaultPurpose={
                    readOnly
                      ? COMMUNICATION_PURPOSE.CLOSED_LOOP_AVAILABLE
                      : COMMUNICATION_PURPOSE.CONSULT_NOTE_NEEDED
                  }
                  defaultReceiverUid={
                    readOnly
                      ? (referral?.createdByUid ?? undefined)
                      : (workflow?.scheduling?.consultantId ?? undefined)
                  }
                />
              </div>
            </div>
          )}

          <div className="h-4" />
        </div>

        {/* Sticky footer — SEEN referrals only.
            specialist_admin: "Close Referral Loop" (SEEN → CLOSED_LOOP).
            readOnly (CLOSED_LOOP): no footer shown. */}
        {!readOnly && isSeen && (
          <div className="flex-shrink-0 border-t border-slate-800/60 px-6 py-5 bg-slate-900/95 backdrop-blur-sm">
            <label className="block text-xs font-medium text-slate-400 mb-2">
              Consult Note <span className="text-slate-600 font-normal">(optional · non-PHI only)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={saving}
              rows={2}
              placeholder="e.g. Reviewed, follow-up in 3 months…"
              data-learning-target="specialist-admin-consult-note"
              className={`w-full px-3 py-2 rounded-xl bg-slate-800/60 border border-slate-700/50 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/40 resize-none disabled:opacity-50 transition-colors mb-3${activeFocusTarget === 'specialist-admin-consult-note' ? ' ring-2 ring-emerald-400/20' : ''}`}
            />
            {isSeen && !note.trim() && !hasConsultDoc && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-300">
                  A consult note or an uploaded consult document is required to close the referral loop.
                </p>
              </div>
            )}
            {saveError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-3">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300">{saveError}</p>
              </div>
            )}
            <button
              type="button"
              onClick={handleCloseLoop}
              disabled={saving || (isSeen && !note.trim() && !hasConsultDoc)}
              data-learning-target="specialist-admin-close-loop-btn"
              className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-semibold transition-colors${activeFocusTarget === 'specialist-admin-close-loop-btn' ? ' ring-2 ring-emerald-400/20' : ''}`}
            >
              {saving
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Closing loop…</>
                : <><CheckCircle2 className="w-5 h-5" /> Close Referral Loop</>}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Seen Referral Card ─────────────────────────────────────────────────────────
// Used in Today and Needs Close Loop tabs.

function SeenReferralCard({ item, onClick, needsClosure = false }) {
  const { referral, workflow } = item;
  const priority       = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const finalOutcome   = referral?.finalOutcome ?? referral?.outcome?.status ?? null;
  const outcomeCfg     = finalOutcome ? (OUTCOME_CONFIG[finalOutcome] ?? null) : null;
  const consultantName = workflow?.scheduling?.consultantName ?? workflow?.scheduling?.consultantId ?? null;
  const apptDate       = workflow?.scheduling?.appointmentDate ?? null;
  const apptTime       = workflow?.scheduling?.appointmentTime ?? null;
  const isUrgent       = priority === WORKFLOW_PRIORITY.URGENT;
  const isHigh         = priority === WORKFLOW_PRIORITY.HIGH;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-start gap-4 px-5 py-4 rounded-xl border transition-all group
        ${needsClosure
          ? 'bg-amber-500/5 border-amber-500/20 hover:bg-amber-500/10 hover:border-amber-500/40'
          : isUrgent
          ? 'bg-red-500/5 border-red-500/20 hover:bg-red-500/10'
          : isHigh
          ? 'bg-orange-500/5 border-orange-500/20 hover:bg-orange-500/10'
          : 'bg-slate-900/60 border-slate-700/50 hover:bg-slate-800/50 hover:border-slate-600/60'}`}
    >
      {/* Icon */}
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 border
        ${needsClosure
          ? 'bg-amber-500/10 border-amber-500/20'
          : 'bg-purple-500/10 border-purple-500/20'}`}>
        <Eye className={`w-4 h-4 ${needsClosure ? 'text-amber-400' : 'text-purple-400'}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {fmtLabel(referral?.specialtyId)}
          {referral?.packId && <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs text-slate-500 font-mono">{referral?.id ?? '—'}</span>
          {PRIORITY_CONFIG[priority] && <Badge {...PRIORITY_CONFIG[priority]} />}
          {outcomeCfg && <Badge tw={outcomeCfg.tw} label={outcomeCfg.label} />}
          <Badge tw="bg-purple-500/10 text-purple-400 border-purple-500/20" label="Seen" />
          {needsClosure && (
            <span className="inline-flex items-center gap-1 text-[11px] text-amber-400 font-medium">
              <AlertTriangle className="w-3 h-3" /> Needs closure
            </span>
          )}
        </div>
        <div className="mt-2 space-y-0.5">
          {apptDate ? (
            <p className={`text-xs flex items-center gap-1 ${needsClosure ? 'text-amber-400/70' : 'text-purple-300'}`}>
              <Calendar className="w-3 h-3" />
              Appointment: {new Date(apptDate + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
              })}{apptTime ? ` · ${from24h(apptTime)}` : ''}
            </p>
          ) : null}
          {consultantName && (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <User className="w-3 h-3" /> Specialist: {consultantName}
            </p>
          )}
        </div>
      </div>

      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 flex-shrink-0 transition-colors mt-2" />
    </button>
  );
}

// ── Closed Loop Card ───────────────────────────────────────────────────────────

function ClosedLoopCard({ item, onClick }) {
  const { referral, workflow } = item;
  const priority       = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const finalOutcome   = referral?.finalOutcome ?? referral?.outcome?.status ?? null;
  const outcomeCfg     = finalOutcome ? (OUTCOME_CONFIG[finalOutcome] ?? null) : null;
  const closedAt       = workflow?.closedLoop?.closedAt;
  const consultantName = workflow?.scheduling?.consultantName ?? workflow?.scheduling?.consultantId ?? null;
  const apptDate       = workflow?.scheduling?.appointmentDate;
  const apptTime       = workflow?.scheduling?.appointmentTime ?? null;

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex items-start gap-4 px-5 py-4 rounded-xl border transition-all group bg-slate-900/60 border-slate-700/50 hover:bg-slate-800/50 hover:border-slate-600/60"
    >
      <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">
          {fmtLabel(referral?.specialtyId)}
          {referral?.packId && <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs text-slate-500 font-mono">{referral?.id ?? '—'}</span>
          {priority !== WORKFLOW_PRIORITY.NORMAL && PRIORITY_CONFIG[priority] && (
            <Badge {...PRIORITY_CONFIG[priority]} />
          )}
          {outcomeCfg && <Badge tw={outcomeCfg.tw} label={outcomeCfg.label} />}
        </div>
        <div className="mt-2 space-y-0.5">
          {closedAt && (
            <p className="text-xs text-emerald-300/70 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Closed {fmtTs(closedAt)}
            </p>
          )}
          {apptDate && (
            <p className="text-xs text-slate-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Appointment: {new Date(apptDate + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
              })}{apptTime ? ` · ${from24h(apptTime)}` : ''}
            </p>
          )}
          {consultantName && (
            <p className="text-xs text-slate-400 flex items-center gap-1">
              <User className="w-3 h-3" /> Specialist: {consultantName}
            </p>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-emerald-400 flex-shrink-0 transition-colors mt-2" />
    </button>
  );
}

// ── Tab Bar ───────────────────────────────────────────────────────────────────

function TabBar({ active, counts, onChange }) {
  return (
    <div className="flex gap-1 border-b border-slate-800/60 mb-6">
      {TABS.map(({ id, label, activeColor }) => {
        const isActive = active === id;
        const count    = counts[id] ?? 0;
        return (
          <button
            key={id}
            onClick={() => onChange(id)}
            className={[
              'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
              isActive ? `${activeColor} bg-transparent` : 'text-slate-500 border-transparent hover:text-slate-300',
            ].join(' ')}
          >
            {label}
            <span className={[
              'text-xs px-1.5 py-0.5 rounded-full font-semibold',
              isActive ? 'bg-slate-700/80' : 'bg-slate-800/60 text-slate-600',
              id === 'needs_close_loop' && count > 0 && !isActive ? 'bg-amber-500/20 text-amber-400' : '',
            ].join(' ')}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Load More ──────────────────────────────────────────────────────────────────

function LoadMoreRow({ remaining, onLoad }) {
  return (
    <div className="pt-2 pb-1 flex flex-col items-center gap-1">
      <button type="button" onClick={onLoad}
        className="px-5 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-700/60 border border-slate-700/40 hover:border-slate-600/60 text-slate-400 hover:text-white text-sm font-medium transition-all">
        Load more
      </button>
      <p className="text-[11px] text-slate-600">{remaining} remaining</p>
    </div>
  );
}

// ── Tab empty state ────────────────────────────────────────────────────────────

function TabEmptyState({ tabId }) {
  const tab = TABS.find((t) => t.id === tabId);
  if (!tab) return null;
  const Icon = tab.emptyIcon;
  return (
    <div className="text-center py-16">
      <Icon className="w-10 h-10 text-slate-700 mx-auto mb-3" />
      <p className="text-slate-500 font-medium">{tab.emptyTitle}</p>
      <p className="text-slate-600 text-sm mt-1">{tab.emptyBody}</p>
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export default function SpecialistAdminWorkplaceView({ initialTab, activeFocusTarget, onLearningContext, autoOpenReferralId }) {
  const { user } = useAuth();
  const tenantId        = user?.tenantId;
  const uid             = user?.uid;
  const role            = user?.role ?? 'specialist_admin';
  const specialtyIds    = user?.specialtyIds ?? [];
  const specialtyIdsKey = specialtyIds.slice().sort().join(',');

  const [activeTab,    setActiveTab]    = useState(initialTab || 'today');
  const [selectedItem, setSelectedItem] = useState(null);

  // ── SEEN items (Today + Needs Close Loop) ───────────────────────────────────
  const [seenItems,    setSeenItems]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(null);
  const [todayVisible, setTodayVisible] = useState(PAGE_SIZE);
  const [needsVisible, setNeedsVisible] = useState(PAGE_SIZE);

  // ── Closed Loop tab (lazy) ──────────────────────────────────────────────────
  const [closedItems,        setClosedItems]        = useState([]);
  const [closedLoaded,       setClosedLoaded]       = useState(false);
  const [closedLoading,      setClosedLoading]      = useState(false);
  const [closedError,        setClosedError]        = useState(null);
  const [closedVisibleCount, setClosedVisibleCount] = useState(PAGE_SIZE);

  // Reset pagination on tab switch
  useEffect(() => {
    setTodayVisible(PAGE_SIZE);
    setNeedsVisible(PAGE_SIZE);
    setClosedVisibleCount(PAGE_SIZE);
  }, [activeTab]);

  // ── Load SEEN referrals ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!tenantId || !uid) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listWorkqueueItems(tenantId, { pageSize: 200 });
      const mySpecialtyIds = specialtyIdsKey ? specialtyIdsKey.split(',') : [];
      // specialist_admin sees all consultants in their specialties — no consultantId filter.
      const seen = data.filter(({ referral, workflow }) =>
        workflow?.status === WORKFLOW_STATUS.SEEN &&
        (mySpecialtyIds.length === 0 || mySpecialtyIds.includes(referral?.specialtyId))
      );
      setSeenItems(seen);
    } catch (err) {
      console.error('[SpecialistAdminWorkplace] load failed:', err);
      setLoadError(safeUserError(err) ?? 'Failed to load workplace. Please retry.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, uid, specialtyIdsKey]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: open the exact referral when arriving from "Open Referral" (Referral
  // Pings). Fires once after the active (SEEN) pool loads; opens the
  // AdminCloseLoopDrawer. Shows a notice if the referral isn't in the active pool
  // (e.g. already closed — Closed-tab lazy search is a later refinement).
  const [deepLinkNotice, setDeepLinkNotice] = useState('');
  const autoOpenFired = useRef(false);
  useEffect(() => {
    if (!autoOpenReferralId || autoOpenFired.current || loading) return;
    autoOpenFired.current = true;
    const res = findReferralInBuckets([{ tab: 'needs_close_loop', items: seenItems }], autoOpenReferralId);
    if (res.found) {
      setActiveTab('needs_close_loop');
      setSelectedItem(res.item);
    } else {
      // Not in the active SEEN pool — read-only fallback (non-PHI core) for a
      // specific reason instead of a vague "not available".
      getReferral(tenantId, autoOpenReferralId)
        .then((ref) => setDeepLinkNotice(classifyDeepLinkFailure({ referral: ref })))
        .catch((err) => setDeepLinkNotice(classifyDeepLinkFailure({ error: err })));
    }
  }, [autoOpenReferralId, loading, seenItems, tenantId]);

  // Remove item from local state once loop is closed
  const handleCompleted = useCallback((referralId) => {
    setSeenItems((prev) => prev.filter((item) => item.referral?.id !== referralId));
    setSelectedItem(null);
  }, []);

  const selectedIsSeen = selectedItem?.workflow?.status === WORKFLOW_STATUS.SEEN;

  useEffect(() => {
    if (onLearningContext) {
      onLearningContext({
        specialistAdminClosingDrawer: !!selectedItem && selectedIsSeen && (activeTab === 'today' || activeTab === 'needs_close_loop'),
        specialistAdminTodayTab: !selectedItem && activeTab === 'today',
        specialistAdminNeedsCloseLoopTab: !selectedItem && activeTab === 'needs_close_loop',
        specialistAdminClosedLoopTab: !selectedItem && activeTab === 'closed_loop',
      });
    }
    return () => { if (onLearningContext) onLearningContext({}); };
  }, [activeTab, selectedItem, selectedIsSeen, onLearningContext]);

  useEffect(() => {
    if (!activeFocusTarget) return;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-learning-target="${activeFocusTarget}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    return () => clearTimeout(timer);
  }, [activeFocusTarget]);

  const hl = (id) => activeFocusTarget === id ? 'ring-2 ring-emerald-400/20' : '';

  // ── Load Closed Loop referrals (lazy) ───────────────────────────────────────
  const loadClosed = useCallback(async () => {
    if (!tenantId || !uid) return;
    setClosedLoading(true);
    setClosedError(null);
    try {
      const data = await listWorkqueueItems(tenantId, { pageSize: 200 });
      const mySpecialtyIds = specialtyIdsKey ? specialtyIdsKey.split(',') : [];
      setClosedItems(sortClosed(data.filter(({ referral, workflow }) =>
        workflow?.status === WORKFLOW_STATUS.CLOSED_LOOP &&
        (mySpecialtyIds.length === 0 || mySpecialtyIds.includes(referral?.specialtyId))
      )));
      setClosedLoaded(true);
    } catch (err) {
      console.error('[SpecialistAdminWorkplace] loadClosed failed:', err);
      setClosedError(safeUserError(err) ?? 'Failed to load closed referrals. Please retry.');
    } finally {
      setClosedLoading(false);
    }
  }, [tenantId, uid, specialtyIdsKey]);

  // Lazy load on first open
  useEffect(() => {
    if (activeTab === 'closed_loop' && !closedLoaded) loadClosed();
  }, [activeTab, closedLoaded, loadClosed]);

  // ── Partition SEEN items into Today / Needs Close Loop ─────────────────────
  const todayItems = [...seenItems.filter((i) => classifySeenItem(i) === 'today')].sort(sortToday);
  const needsItems = [...seenItems.filter((i) => classifySeenItem(i) === 'needs_close_loop')].sort(sortNeedsCloseLoop);

  const tabCounts = {
    today:            todayItems.length,
    needs_close_loop: needsItems.length,
    closed_loop:      closedItems.length,
  };

  const selectedIsCompleted = selectedItem?.workflow?.status === WORKFLOW_STATUS.CLOSED_LOOP;

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-500/20 flex items-center justify-center">
            <Stethoscope className="w-5 h-5 text-sky-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-white">My Workplace</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Review seen patients and close referral loops.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white text-sm transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-sky-400 animate-spin mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Loading workplace…</p>
          </div>
        </div>
      )}

      {/* Load error */}
      {!loading && loadError && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
          <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-red-300 font-medium">Failed to load workplace</p>
            <p className="text-xs text-red-400/80 mt-0.5">{loadError}</p>
            <button onClick={load} className="mt-2 text-xs text-red-300 underline hover:no-underline">Retry</button>
          </div>
        </div>
      )}

      {deepLinkNotice && (
        <div className="mb-4 flex items-start gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="flex-1 text-sm text-amber-300">{deepLinkNotice}</p>
          <button onClick={() => setDeepLinkNotice('')} className="text-xs text-amber-400/80 hover:text-amber-300">Dismiss</button>
        </div>
      )}

      {/* Tabs + Content */}
      {!loading && !loadError && (
        <>
          <TabBar active={activeTab} counts={tabCounts} onChange={setActiveTab} />

          {/* Today tab */}
          {activeTab === 'today' && (
            <div data-learning-target="specialist-admin-today-tab" className={`space-y-2 rounded-xl transition-all ${hl('specialist-admin-today-tab')}`}>
              {todayItems.length === 0 ? (
                <TabEmptyState tabId="today" />
              ) : (
                <>
                  {todayItems.slice(0, todayVisible).map((item, idx) => (
                    <div key={item.referral.id} {...(idx === 0 ? { 'data-learning-target': 'specialist-admin-referral-card', className: `rounded-xl transition-all ${hl('specialist-admin-referral-card')}` } : {})}>
                      <SeenReferralCard
                        item={item}
                        needsClosure={false}
                        onClick={() => setSelectedItem(item)}
                      />
                    </div>
                  ))}
                  {todayItems.length - todayVisible > 0 && (
                    <LoadMoreRow
                      remaining={todayItems.length - todayVisible}
                      onLoad={() => setTodayVisible((c) => c + PAGE_SIZE)}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* Needs Close Loop tab */}
          {activeTab === 'needs_close_loop' && (
            <div data-learning-target="specialist-admin-needs-close-loop-tab" className={`space-y-2 rounded-xl transition-all ${hl('specialist-admin-needs-close-loop-tab')}`}>
              {needsItems.length === 0 ? (
                <TabEmptyState tabId="needs_close_loop" />
              ) : (
                <>
                  {needsItems.slice(0, needsVisible).map((item, idx) => (
                    <div key={item.referral.id} {...(idx === 0 ? { 'data-learning-target': 'specialist-admin-needs-close-loop-card', className: `rounded-xl transition-all ${hl('specialist-admin-needs-close-loop-card')}` } : {})}>
                      <SeenReferralCard
                        item={item}
                        needsClosure
                        onClick={() => setSelectedItem(item)}
                      />
                    </div>
                  ))}
                  {needsItems.length - needsVisible > 0 && (
                    <LoadMoreRow
                      remaining={needsItems.length - needsVisible}
                      onLoad={() => setNeedsVisible((c) => c + PAGE_SIZE)}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* Closed Loop tab (lazy) */}
          {activeTab === 'closed_loop' && (
            <div data-learning-target="specialist-admin-closed-loop-tab" className={`space-y-2 rounded-xl transition-all ${hl('specialist-admin-closed-loop-tab')}`}>
              {closedLoading && (
                <div className="flex items-center justify-center py-24">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Loading closed referrals…</p>
                  </div>
                </div>
              )}
              {!closedLoading && closedError && (
                <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-red-300 font-medium">Failed to load closed referrals</p>
                    <p className="text-xs text-red-400/80 mt-0.5">{closedError}</p>
                    <button onClick={loadClosed} className="mt-2 text-xs text-red-300 underline hover:no-underline">Retry</button>
                  </div>
                </div>
              )}
              {!closedLoading && !closedError && closedItems.length === 0 && (
                <TabEmptyState tabId="closed_loop" />
              )}
              {!closedLoading && !closedError && closedItems.length > 0 && (
                <div className="space-y-2">
                  {closedItems.slice(0, closedVisibleCount).map((item) => (
                    <ClosedLoopCard
                      key={item.referral.id}
                      item={item}
                      onClick={() => setSelectedItem(item)}
                    />
                  ))}
                  {closedItems.length > closedVisibleCount && (
                    <LoadMoreRow
                      remaining={closedItems.length - closedVisibleCount}
                      onLoad={() => setClosedVisibleCount((c) => c + PAGE_SIZE)}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Admin Close Loop Drawer */}
      {selectedItem && (
        <AdminCloseLoopDrawer
          item={selectedItem}
          uid={uid}
          role={role}
          onClose={() => setSelectedItem(null)}
          onCompleted={handleCompleted}
          readOnly={selectedIsCompleted}
          activeFocusTarget={activeFocusTarget}
        />
      )}
    </div>
  );
}
